import { Fragment, memo } from 'react';

import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { capitalize } from '@Sage/ssm-utils';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';

import { EMAIL_STATUS } from 'constants/Constants';
import { ACTIVITIES, COMPANIES, CONTACTS } from 'constants/Entities';
import { InfoRow, InfoRowSection } from 'containers/components/EntityDetailV2';
import { getLiteral } from 'utils/getLiteral';

import TitleCommentModal from '../../../shared/TitleCommentModal';
import EmailSubjectRow from '../../tabs/Email/EmailSubjectRow';
import ActivityDetailContainer from '../ActivityDetailContainer';

const EmailGeneralDetail = memo(
    ({ activity, emailWithImages, getEmailWithImages, labels, loadingEmailWithImages, shared }) => {
        const theme = useTheme();
        const {
            emailCompanyContent,
            emailContactContent,
            emailOpportunityRow,
            emailRole,
            rightContent,
            titleRow,
        } = shared;

        const emailStatus = activity?.Tracking?.EmailStatus;
        const isSent = getBackendBoolean(activity.IsSent);
        const iconName = isSent ? 'emailSend' : 'emailReceive';
        const labelEmail = isSent
            ? getLiteral('label_emailsent')
            : getLiteral('label_emailreceived');
        const labelFromTo = isSent ? labels.to : labels.from;

        const modalTitle = (
            <TitleCommentModal
                activity={activity}
                label={labelEmail}
                iconName={iconName}
                items={[
                    { entity: CONTACTS, label: labelFromTo },
                    { entity: COMPANIES, label: labelFromTo },
                ]}
            />
        );

        let trackingStatusColor = '';
        let trackingStatusLabel = '';

        switch (emailStatus) {
            case EMAIL_STATUS.PENDING:
                trackingStatusColor = theme.colors.semantic.caution500;
                trackingStatusLabel = getLiteral('label_email_status_pending');
                break;
            case EMAIL_STATUS.ERROR:
                trackingStatusColor = theme.colors.semantic.negative500;
                trackingStatusLabel = getLiteral('label_email_status_error');
                break;
            case EMAIL_STATUS.SENT:
                trackingStatusColor = theme.colors.semantic.positive500;
                trackingStatusLabel = getLiteral('label_email_status_success');
                break;
            default:
                break;
        }

        const showStatus = trackingStatusLabel && !activity.IsReceived;

        return (
            <ActivityDetailContainer
                activity={activity}
                content={{
                    main: (
                        <InfoRowSection>
                            <EmailSubjectRow
                                id={activity.Id}
                                label={labels.subject}
                                value={activity.Subject}
                                text={emailWithImages || activity.Body}
                                modalTitle={modalTitle}
                                entity={ACTIVITIES}
                                attachments={activity.attachments}
                                getEmailWithImages={getEmailWithImages}
                                loadingEmailWithImages={loadingEmailWithImages}
                                emailWithImages={emailWithImages}
                            />
                        </InfoRowSection>
                    ),
                    right: (
                        <Fragment>
                            {titleRow}
                            <InfoRowSection>
                                <InfoRow
                                    label={capitalize(labelFromTo)}
                                    value={emailContactContent}
                                    rightContent={rightContent}
                                />
                                <InfoRow label={labels.account} value={emailCompanyContent} />
                                <InfoRow label={labels.role} value={emailRole} />
                                {showStatus && (
                                    <InfoRow
                                        label={labels.emailStatus}
                                        value={
                                            <Text color={trackingStatusColor}>
                                                {trackingStatusLabel}
                                            </Text>
                                        }
                                    />
                                )}
                                {emailOpportunityRow}
                            </InfoRowSection>
                        </Fragment>
                    ),
                }}
            />
        );
    },
);

EmailGeneralDetail.displayName = 'EmailGeneralDetail';

export default EmailGeneralDetail;
